import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { formatCharacterLabels } from "../../utilities/MaterialDetails";
import { fullImageUrl } from "../../utilities/imageGrant";
import { MaterialContext } from "../../context/MaterialContext";
import modalStyle from "../../styles/modal.module.scss";
import CollectionButton from "../CollectionButton";
import LoadingDots from "../LoadingDots";
import { editIcon } from "../Icons";
import { Swiper, SwiperSlide } from "swiper/react";
import MobileChromeBar from "./MobileChromeBar";
import "swiper/css";

export default function MobileMaterialModal({
  handleCloseModal,
  onPrimaryAction,
  primaryLabel = "Similar Materials",
  materialData,
  vendor,
  title,
  description,
  handleChangeImage,
  ROOT_SERVER,
  slug,
  loaded,
  imageNumber,
  canEdit = false,
  onEdit,
}) {
  const { imageGrant } = useContext(MaterialContext);
  const [imageIndex, setImageIndex] = useState(imageNumber || 0);
  const [isLandscape, setIsLandscape] = useState(false);
  const [loadedByIndex, setLoadedByIndex] = useState({});
  const swiperRef = useRef(null);

  const images = Array.isArray(materialData?.images) ? materialData.images : [];
  const characterLabels = formatCharacterLabels(materialData?.character);
  const feature = Array.isArray(materialData?.feature)
    ? materialData.feature
    : [];

  const priceValue = useMemo(() => {
    const value = Number(materialData?.price);
    if (Number.isFinite(value)) {
      return value.toFixed(2);
    }
    return "--";
  }, [materialData?.price]);

  useEffect(() => {
    setImageIndex(imageNumber || 0);
  }, [imageNumber]);

  const markImageLoaded = (index) => {
    setLoadedByIndex((prev) => {
      if (prev[index]) {
        return prev;
      }

      return { ...prev, [index]: true };
    });
  };

  const currentImageLoaded = Boolean(loadedByIndex[imageIndex]);

  const bindImageLoad = (index) => (image) => {
    if (image?.complete && image.naturalWidth > 0) {
      Promise.resolve().then(() => markImageLoaded(index));
    }
  };

  const handleSlideChange = (swiper) => {
    const nextIndex = swiper.activeIndex;
    setImageIndex(nextIndex);
    handleChangeImage(nextIndex);
  };

  const handleDotClick = (index) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
      return;
    }

    setImageIndex(index);
    handleChangeImage(index);
  };

  const bindSwiper = (swiper) => {
    swiperRef.current = swiper;
  };

  useEffect(() => {
    const updateOrientation = () => {
      if (typeof window === "undefined") {
        return;
      }

      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };

    updateOrientation();
    window.addEventListener("resize", updateOrientation);
    window.addEventListener("orientationchange", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateOrientation);
      window.removeEventListener("orientationchange", updateOrientation);
    };
  }, []);

  const renderLandscape = () => (
    <div className={modalStyle.mobileLandscapeShell}>
      <div className={modalStyle.mobileLandscapeHeader}>
        <div className={modalStyle.mobileCollectionAction}>
          <CollectionButton materialId={materialData.id} />
        </div>
      </div>

      <div className={modalStyle.mobileLandscapeImageWrap}>
        <Swiper
          className={modalStyle.mobileSwiper}
          loop={false}
          nested={false}
          resistanceRatio={0.65}
          touchStartPreventDefault={false}
          initialSlide={imageIndex}
          onSwiper={bindSwiper}
          onSlideChange={handleSlideChange}
        >
          {images.length >= 1
            ? images.map((item, i) => (
                <SwiperSlide
                  key={item + i}
                  className={modalStyle.mobileLandscapeSlide}
                >
                  <img
                    alt={item}
                    className={`${modalStyle.materialImage} ${modalStyle.mobileLandscapeImage}`}
                    src={fullImageUrl(vendor, slug, item, imageGrant)}
                    onLoad={() => markImageLoaded(i)}
                    onError={() => markImageLoaded(i)}
                    ref={bindImageLoad(i)}
                    draggable={false}
                  />
                </SwiperSlide>
              ))
            : false}
        </Swiper>
        {!currentImageLoaded && images.length > 0 ? (
          <div className={modalStyle.mobileImageLoader}>
            <LoadingDots dotsClassName={modalStyle.mobileImageLoaderDots} />
          </div>
        ) : null}
      </div>

      <div className={modalStyle.mobileLandscapeTitleBar}>
        <div className={modalStyle.mobileLandscapeTitleInline}>
          <span className={modalStyle.mobileLandscapeVendorText}>{vendor}</span>
          <span
            className={modalStyle.mobileLandscapeTitleDot}
            aria-hidden="true"
          >
            •
          </span>
          <h3 className={modalStyle.mobileTitle}>{title}</h3>
        </div>
        {canEdit ? (
          <button
            type="button"
            className={modalStyle.vendorToolsButton}
            onClick={onEdit}
          >
            {editIcon}
            <span>Edit</span>
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={`${modalStyle.modalShade} ${modalStyle.mobileShade}`}
      onClick={handleCloseModal}
    >
      {loaded ? (
        <div
          className={`${modalStyle.materialModal} ${modalStyle.mobileModal}`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={title || "Material details"}
        >
          {isLandscape ? (
            renderLandscape()
          ) : (
            <>
              <MobileChromeBar
                compact
                logoLinked={false}
                onClose={handleCloseModal}
              />

              <div
                className={`${modalStyle.modalContent} ${modalStyle.mobileContent}`}
              >
                {/* IMAGE CONTAINER START */}
                <div
                  className={`${modalStyle.imageContainer} ${modalStyle.mobileImageContainer}`}
                >
                  <div className={modalStyle.mobileHeaderRow}>
                    <div className={modalStyle.mobileVendorBadge}>{vendor}</div>
                    <div className={modalStyle.mobileCollectionAction}>
                      <CollectionButton materialId={materialData.id} />
                    </div>
                  </div>

                  <Swiper
                    className={modalStyle.mobileSwiper}
                    loop={false}
                    nested={false}
                    resistanceRatio={0.65}
                    touchStartPreventDefault={false}
                    initialSlide={imageIndex}
                    onSwiper={bindSwiper}
                    onSlideChange={handleSlideChange}
                  >
                    {images.length >= 1
                      ? images.map((item, i) => (
                          <SwiperSlide
                            key={item + i}
                            className={modalStyle.mobileSwiperSlide}
                          >
                            <img
                              alt={item}
                              className={`${modalStyle.materialImage} ${modalStyle.mobileHeroImage}`}
                              src={fullImageUrl(vendor, slug, item, imageGrant)}
                              onLoad={() => markImageLoaded(i)}
                              onError={() => markImageLoaded(i)}
                              ref={bindImageLoad(i)}
                              draggable={false}
                            />
                          </SwiperSlide>
                        ))
                      : false}
                  </Swiper>

                  {!currentImageLoaded && images.length > 0 ? (
                    <div className={modalStyle.mobileImageLoader}>
                      <LoadingDots
                        dotsClassName={modalStyle.mobileImageLoaderDots}
                      />
                    </div>
                  ) : null}

                  {images.length > 1 ? (
                    <div className={modalStyle.mobileSwatchRow}>
                      {images.map((item, i) => (
                        <button
                          key={item + i}
                          type="button"
                          className={`${modalStyle.mobileSwatchDot} ${
                            imageIndex === i
                              ? modalStyle.mobileSwatchDotActive
                              : ""
                          }`}
                          onClick={() => handleDotClick(i)}
                          aria-label={`Show image ${i + 1}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
                {/* IMAGE CONTAINER END */}
              </div>

              <div className={modalStyle.mobileIdentity}>
                <h3 className={modalStyle.mobileTitle}>{title}</h3>
                <p className={modalStyle.mobileDescription}>{description}</p>
              </div>

              <div className={modalStyle.mobileDataWrap}>
                <div className={modalStyle.modalMetaGrid}>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Substrate</span>
                    <strong>{materialData?.substrate || "--"}</strong>
                  </div>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Type</span>
                    <strong>{materialData?.type || "--"}</strong>
                  </div>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Group</span>
                    <strong>{materialData?.subtype || "--"}</strong>
                  </div>
                </div>

                <div className={modalStyle.modalPriceBlock}>
                  <div className={modalStyle.modalPriceStatusRow}>
                    <div className={modalStyle.modalPriceSide}>
                      <div className={modalStyle.modalPriceRow}>
                        <span className={modalStyle.modalPriceValue}>
                          ${priceValue}
                        </span>
                        <span className={modalStyle.modalPriceUom}>
                          / sq.ft.
                        </span>
                      </div>
                      <p className={modalStyle.modalPriceNote}>
                        Price shown for reference only.
                      </p>
                    </div>
                    <div className={modalStyle.modalStatusSide}>
                      <span className={modalStyle.modalStatusLabel}>
                        Status
                      </span>
                      <strong className={modalStyle.modalStatusValue}>
                        {materialData?.status || "--"}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className={modalStyle.mobileTraitGrid}>
                  <section>
                    <h4>Character</h4>
                    <p>
                      {characterLabels.length > 0
                        ? characterLabels.join(" · ")
                        : "Not available"}
                    </p>
                  </section>
                  <section>
                    <h4>Finish</h4>
                    <p>
                      {feature.length > 0
                        ? feature.join(" · ")
                        : "Not available"}
                    </p>
                  </section>
                </div>

                {canEdit ? (
                  <div className={modalStyle.vendorToolsBox}>
                    <div className={modalStyle.vendorToolsMeta}>
                      <div className={modalStyle.vendorToolsRow}>
                        <span className={modalStyle.vendorToolsLabel}>
                          View Count
                        </span>
                        <strong>{materialData?.view_count || 0}</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={modalStyle.vendorToolsButton}
                      onClick={onEdit}
                    >
                      {editIcon}
                      <span>Edit</span>
                    </button>
                  </div>
                ) : null}

                <div className={modalStyle.mobileFooterActions}>
                  <button
                    type="button"
                    className={`${modalStyle.mobileFooterButton} ${
                      !onPrimaryAction ? modalStyle.actionDisabled : ""
                    }`}
                    onClick={onPrimaryAction}
                  >
                    <svg
                      className={modalStyle.mobileFooterIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 4.5L12.5 10L7 15.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <h4>{primaryLabel}</h4>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          className={`${modalStyle.materialModal} ${modalStyle.mobileModal}`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Loading material details"
        >
          <MobileChromeBar compact logoLinked={false} onClose={handleCloseModal} />
          <div className={modalStyle.mobileLoadingShell}>
            <LoadingDots
              label="Loading Material Details..."
              labelClassName={modalStyle.modalStateTitle}
              dotsClassName={modalStyle.modalStateDots}
            />
          </div>
        </div>
      )}
    </div>
  );
}
